import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasRouteAccess, isRestrictedUser, isWorkshopUser } from '../utils/workshopAccess';

interface WorkshopGuardedRouteProps {
  children: React.ReactNode;
}

export function WorkshopGuardedRoute({ children }: WorkshopGuardedRouteProps) {
  const { user } = useAuth();
  const location = useLocation();

  // If user is not a restricted user (workshop or freemium), allow access
  if (!isRestrictedUser(user)) {
    return <>{children}</>;
  }

  // Check if restricted user has access to this route
  if (!hasRouteAccess(user, location.pathname)) {
    const isWorkshop = isWorkshopUser(user);
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="mb-6">
            <div className="w-20 h-20 mx-auto mb-4 bg-orange-100 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-3">Growth Boardroom Only</h1>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              {isWorkshop 
                ? 'This tool is exclusively available to Growth Boardroom members. Your Workshop access includes a curated set of essential business tools to get you started.'
                : 'This tool is exclusively available to Growth Boardroom members. Your access includes Focus Finder and Finance tools to get you started.'}
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">
                {isWorkshop ? 'Available Workshop Tools:' : 'Available Tools:'}
              </h3>
              <ul className="text-sm text-blue-800 space-y-1 text-left">
                {isWorkshop ? (
                  <>
                    <li>• Focus Finder Assessment</li>
                    <li>• Customer Avatar Builder</li>
                    <li>• 13-Week Cashflow Plan</li>
                    <li>• Company Creed Tool</li>
                    <li>• Vision & Strategic Planning</li>
                    <li>• And more essential tools...</li>
                  </>
                ) : (
                  <>
                    <li>• Focus Finder Assessment</li>
                    <li>• 13-Week Cashflow Plan</li>
                    <li>• Cashflow Acceleration Process</li>
                    <li>• Zero-Based Budgeting</li>
                    <li>• Vendor Discounts</li>
                    <li>• Dynamic P&L</li>
                    <li>• Dynamic Financial Dashboard</li>
                  </>
                )}
              </ul>
            </div>
            <div className="space-y-3">
              <button
                onClick={() => window.history.back()}
                className="w-full bg-primary text-primary-foreground px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors"
              >
                Go Back
              </button>
              <button
                onClick={() => window.location.href = '/dashboard'}
                className="w-full border border-border px-6 py-3 rounded-lg hover:bg-accent transition-colors"
              >
                Return to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
} 